import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import type { CopyStatus } from '@/hooks/useCopyToClipboard';
import { MOBILE_NAMESPACE } from '@/i18n';
import '@/Pages/Mobile/MobilePage.sass';

export type CopyLinkRowProps = {
  url: string;
  onCopy: (url: string) => Promise<boolean>;
  status: CopyStatus;
};

export function CopyLinkRow({
  url,
  onCopy,
  status,
}: CopyLinkRowProps): JSX.Element {
  const { t } = useTranslation(MOBILE_NAMESPACE);
  const isCopied = status === 'copied';
  const buttonLabel = isCopied ? t('copy.copied') : t('copy.button');

  return (
    <Box
      component="section"
      className="mobile-validation-copy-row"
      data-testid="mobile-validation-copy-row"
      aria-labelledby="mobile-copy-url-label"
    >
      <Typography
        id="mobile-copy-url-label"
        component="p"
        className="mobile-validation-copy-row__label"
      >
        {t('copy.urlLabel')}
      </Typography>

      <Box className="mobile-validation-copy-row__field">
        <Box
          component="output"
          className="mobile-validation-copy-row__url"
          data-testid="mobile-validation-copy-url"
          aria-readonly="true"
        >
          {url}
        </Box>

        <Button
          type="button"
          variant="contained"
          color="primary"
          className={
            isCopied
              ? 'mobile-validation-copy-row__button mobile-validation-copy-row__button--copied'
              : 'mobile-validation-copy-row__button'
          }
          data-testid="mobile-validation-copy-button"
          startIcon={
            isCopied ? (
              <CheckRoundedIcon aria-hidden fontSize="small" />
            ) : (
              <ContentCopyRoundedIcon aria-hidden fontSize="small" />
            )
          }
          onClick={() => {
            void onCopy(url);
          }}
        >
          {buttonLabel}
        </Button>
      </Box>

      <Typography
        component="p"
        role="status"
        aria-live="polite"
        className="mobile-validation-copy-row__status"
        data-testid="mobile-validation-copy-status"
      >
        {isCopied ? t('copy.copied') : ''}
      </Typography>
    </Box>
  );
}
